/**
 * The header navigation for the public pages: a dropdown for the use-case
 * pages, grouped links for the two exams and the comparison between them, the
 * price, a language dropdown, and a standing "take the test" call to action.
 *
 * The flat links collapse into the panel below `lg`, not below `md`. Three exam
 * links plus the price plus the dropdown do not fit a tablet: at 800px the
 * labels wrapped onto two lines each and the bar grew to twice its height.
 *
 * Hand-rolled dropdowns rather than Radix, for the same reason as `AppMenu` and
 * the language switcher: the header is `position: sticky` and the floating
 * machinery placed the panel off-screen there. On a phone the whole thing
 * collapses into one panel under the bar.
 */
import { useEffect, useRef, useState, type RefObject } from "react";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/link";
import { LOCALES, useI18n } from "@/i18n";
import type { MessageKey } from "@/i18n";
import { navigate, useRoute } from "@/lib/router";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { BEFORE_LINKS, EXAM_MENUS, USE_CASE_LINKS, type NavGroup, type NavLink } from "./links";

/** Close `open` on a click outside `ref` or on Escape. */
function useDismiss(
  open: boolean,
  setOpen: (v: boolean) => void,
  ref: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open, setOpen, ref]);
}

const triggerClass =
  "inline-flex items-center gap-1 whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-foreground";

/** A flat header link, muted until hovered. */
function TopLink({ to, label }: NavLink) {
  const { t } = useI18n();
  return (
    <Link
      to={to}
      className="whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      {t(label)}
    </Link>
  );
}

/** One header dropdown — opens on hover and on click, closes on either, on a
 *  click outside, or on Escape. */
function Dropdown({ label, groups, showGroupLabels = true }: { label: MessageKey; groups: NavGroup[]; showGroupLabels?: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, setOpen, box);

  return (
    <div
      ref={box}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        className={triggerClass}
      >
        {t(label)}
        <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-2">
          <div role="menu" className="min-w-64 rounded-md border bg-popover p-2 shadow-lg">
            {groups.map((group, index) => (
              <div key={group.label} className={index ? "mt-2 border-t pt-2" : ""}>
                {showGroupLabels && <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(group.label)}
                </p>}
                {group.links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="block rounded px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    {t(l.label)}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** The language switch as a dropdown — the short code in the bar, full names in
 *  the panel. */
function LanguageMenu() {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, setOpen, box);
  const current = LOCALES.find((l) => l.id === locale)!;

  return (
    <div
      ref={box}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t("nav.language")}
        onClick={() => setOpen(!open)}
        className={triggerClass}
      >
        {current.short}
        <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute right-0 top-full pt-2">
          <div role="menu" className="min-w-40 rounded-md border bg-popover p-1 shadow-lg">
            {LOCALES.map((l) => (
              <button
                key={l.id}
                type="button"
                role="menuitemradio"
                aria-checked={l.id === locale}
                lang={l.id}
                onClick={() => {
                  setLocale(l.id);
                  setOpen(false);
                }}
                className={cn(
                  "block w-full rounded px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                  l.id === locale && "font-semibold",
                )}
              >
                {l.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MarketingNav() {
  const { t, locale, setLocale } = useI18n();
  const path = useRoute();
  const [menuOpen, setMenuOpen] = useState(false);

  // A navigation closes the mobile panel.
  useEffect(() => setMenuOpen(false), [path]);

  const cta = (
    <Button
      size="sm"
      variant="brand"
      className="h-10"
      onClick={() => {
        track("cta_click", { placement: "header", target: "/app" });
        navigate("/app");
      }}
    >
      {t("landing.start")}
      <ArrowRight className="size-4" />
    </Button>
  );

  return (
    <div className="ml-auto flex items-center gap-1">
      <nav className="hidden items-center gap-5 lg:flex">
        <Dropdown label="nav.whoFor" groups={[{ label: "nav.whoFor", links: USE_CASE_LINKS }]} showGroupLabels={false} />
        {EXAM_MENUS.map((menu) => (
          <Dropdown key={menu.label} label={menu.label} groups={menu.groups} />
        ))}
        <TopLink to="/porownanie" label="nav.compare" />
        <TopLink to="/pricing" label="nav.pricing" />
        <LanguageMenu />
        {cta}
      </nav>

      <div className="flex items-center gap-1 lg:hidden">
        {cta}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t("nav.menu")}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X className="size-4" /> : <Menu className="size-4" />}
        </Button>
      </div>

      {menuOpen && (
        <div className="absolute inset-x-0 top-full border-b bg-background shadow-lg lg:hidden">
          <nav className="mx-auto flex max-w-5xl flex-col gap-0.5 px-4 py-3 sm:px-5">
            <p className="px-2 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("nav.whoFor")}
            </p>
            {USE_CASE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="rounded px-2 py-2 pl-4 text-sm font-medium hover:bg-accent"
              >
                {t(l.label)}
              </Link>
            ))}
            {EXAM_MENUS.map((menu) => (
              <details key={menu.label} className="group">
                <summary className="cursor-pointer list-none rounded px-2 py-2 text-sm font-semibold hover:bg-accent">
                  {t(menu.label)}
                  <ChevronDown className="ml-1 inline size-4 transition-transform group-open:rotate-180" />
                </summary>
                {menu.groups.map((group) => (
                  <div key={group.label} className="pb-1 pl-4">
                    <p className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(group.label)}</p>
                    {group.links.map((l) => (
                      <Link key={l.to} to={l.to} className="block rounded px-2 py-2 text-sm hover:bg-accent">
                        {t(l.label)}
                      </Link>
                    ))}
                  </div>
                ))}
              </details>
            ))}
            <Link to="/porownanie" className="rounded px-2 py-2 text-sm font-medium hover:bg-accent">{t("nav.compare")}</Link>
            {BEFORE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="rounded px-2 py-2 text-sm font-medium hover:bg-accent"
              >
                {t(l.label)}
              </Link>
            ))}
            <div className="mt-1 border-t pt-2">
              <p className="px-2 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("nav.language")}
              </p>
              {LOCALES.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  lang={l.id}
                  aria-pressed={l.id === locale}
                  onClick={() => setLocale(l.id)}
                  className={cn(
                    "block w-full rounded px-2 py-2 pl-4 text-left text-sm font-medium hover:bg-accent",
                    l.id === locale ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {l.name}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </div>
  );
}
